import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { updateProjectWorkspacesSelected } from 'Services/user.js';
import Button from 'Components/Atoms/Button/Button.jsx';
import FormProvider, { RHFSelect, RHFInput } from 'Components/Organisms/HookForm';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useCountries from 'Hooks/useCountries.js';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';
import Loading from 'Components/Atoms/Loading/Loading.jsx';
import { useTranslation } from 'react-i18next';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import RadioGroup from 'Components/Atoms/Radio/RadioGroup';
import Radio from 'Components/Atoms/Radio/Radio';
import CollapseContent from 'Components/Atoms/Collapse/CollapseContent';
import Divider from 'Components/Atoms/Divider/Divider';
import AlertForm from 'Components/Molecules/AlertForm/AlertForm';

const normalizeData = (data) => {
    const normalizedData = {};

    for (const key in data) {
        const value = data[key];
        normalizedData[key] = value === '' ? null : value;
    }

    return normalizedData;
};

export default function InvoicesInvoicingConfigDrawer(props) {
    const { onClose, onSubmit, onError, entityTypeRequired = false } = props;

    const { workspace, isLoading } = useWorkspaceSelected();
    const [processing, setProcessing] = useState(false);
    const queryClient = useQueryClient();

    const { countries, isLoading: isLoadingCountries } = useCountries();

    const { t } = useTranslation('settings');

    const countriesOptions = countries.map((country) => ({
        value: country.code,
        label: country.name,
    }));

    const schema = useMemo(() => {
        return yup.object().shape({
            entity_type: entityTypeRequired
                ? yup.string().nullable().required(t('entity-type-required-error'))
                : yup.string().nullable(),
        });
    }, [t, entityTypeRequired]);

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            entity_type: null,
            name: '',
            tax_id: '',
            email: '',
            phone_number: '',
            address: '',
            address_secondary: '',
            city: '',
            postal_code: '',
            state: '',
            country: workspace?.country || 'ESP',
            representative_name: '',
            representative_surname: '',
            representative_tax_id: '',
        },
    });

    const { handleSubmit, reset, watch, control } = methods;

    const entityType = watch('entity_type');
    const showRepresentative = entityType === 'company';

    const submit = async (data) => {
        setProcessing(true);

        try {
            const normalized = normalizeData(data);
            const payload =
                normalized.entity_type === 'company'
                    ? normalized
                    : {
                          ...normalized,
                          representative_name: null,
                          representative_surname: null,
                          representative_tax_id: null,
                      };
            await updateProjectWorkspacesSelected(workspace.id, {
                invoicing_address: payload,
            });
            await queryClient.invalidateQueries({ queryKey: ['project-workspace-selected'] });
            onSubmit();
        } catch (error) {
            onError(error);
        } finally {
            setProcessing(false);
        }
    };

    useEffect(() => {
        reset({
            entity_type: workspace?.invoicing_address?.entity_type ?? null,
            name: workspace?.invoicing_address?.name ?? '',
            tax_id: workspace?.invoicing_address?.tax_id ?? '',
            email: workspace?.invoicing_address?.email ?? '',
            phone_number: workspace?.invoicing_address?.phone_number ?? '',
            address: workspace?.invoicing_address?.address ?? '',
            address_secondary: workspace?.invoicing_address?.address_secondary ?? '',
            city: workspace?.invoicing_address?.city ?? '',
            postal_code: workspace?.invoicing_address?.postal_code ?? '',
            state: workspace?.invoicing_address?.state ?? '',
            country: workspace?.invoicing_address?.country ?? 'ESP',
            representative_name: workspace?.invoicing_address?.representative_name ?? '',
            representative_surname: workspace?.invoicing_address?.representative_surname ?? '',
            representative_tax_id: workspace?.invoicing_address?.representative_tax_id ?? '',
        });
    }, [workspace]);

    if (isLoading) {
        return (
            <div className="py-6 text-center">
                <Loading />
            </div>
        );
    }

    return (
        <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
            <div className="grow mt-4">
                {entityTypeRequired && <AlertForm className="mb-6">{t('entity-type-required-notice')}</AlertForm>}
                <div className="grid gap-6">
                    <RHFInput label={t('legal-name')} name="name" />

                    <RHFInput type="email" label={t('email')} name="email" />

                    <RHFInput type="tel" label={t('phone-number')} name="phone_number" />

                    <RHFInput label={t('tax-id')} name="tax_id" />

                    <Controller
                        name="entity_type"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <div className="grid gap-2">
                                <RadioGroup
                                    name={field.name}
                                    value={field.value ?? ''}
                                    onChange={(ev) => field.onChange(ev.target.value)}
                                >
                                    <Radio value="individual">{t('entity-type-individual')}</Radio>
                                    <Radio value="company">{t('entity-type-company')}</Radio>
                                </RadioGroup>
                                {error && <p className="text-sm text-danger">{error.message}</p>}
                            </div>
                        )}
                    />

                    <CollapseContent open={showRepresentative}>
                        <div className="py-2 grid gap-6">
                            <RHFInput label={t('representative-name')} name="representative_name" maxLength={128} />
                            <RHFInput
                                label={t('representative-surname')}
                                name="representative_surname"
                                maxLength={128}
                            />
                            <RHFInput label={t('representative-tax-id')} name="representative_tax_id" maxLength={64} />
                        </div>
                    </CollapseContent>

                    <Divider className="mb-4 mt-2" />

                    <RHFInput label={t('address')} name="address" />

                    <RHFInput label={t('address-secondary')} name="address_secondary" />

                    <RHFInput label={t('postal-code')} name="postal_code" />

                    <RHFInput label={t('city')} name="city" />

                    <RHFInput label={t('state')} name="state" />

                    {isLoadingCountries ? (
                        <Skeleton variant="rounded" height={56} />
                    ) : (
                        <RHFSelect
                            options={countriesOptions}
                            name="country"
                            label={t('country')}
                            className="min-w-full"
                            enableFilter
                        />
                    )}
                </div>
            </div>

            <div className="flex gap-2 py-5">
                <Button
                    variant="primary"
                    isBusy={processing}
                    type="submit"
                    className="grow"
                    testId="invoicing-config.save"
                >
                    {t('save')}
                </Button>
                <Button className="grow" onClick={onClose} testId="invoicing-config.cancel">
                    {t('cancel')}
                </Button>
            </div>
        </FormProvider>
    );
}

InvoicesInvoicingConfigDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    entityTypeRequired: PropTypes.bool,
};
